import React from 'react';
import { Lock, CircleDot, CheckCircle2, Target as TargetIcon, ArrowRight, Flame } from 'lucide-react';
import { Tooltip } from 'antd';
import { useDashTheme } from '../../DashboardV2/hooks/useDashTheme';
import { Card, Badge } from '../../DashboardV2/components/primitives';

const MonthlyRoadmap = ({ months, onMonthClick, streakIconUrl = null, isOrgAdmin = false, isAutonomousMode = false, currentPhase = null }) => {
  const t = useDashTheme();

  const filteredMonths = (months || []).filter(m => {
    if (m.score != null && m.score !== undefined && parseFloat(m.score) > 0) return true;
    if (m.status === 'active') return true;
    // Completed cycle the user participated in (has approved goals) but which
    // isn't scored yet — its score is 0/present rather than null. Keep the card
    // visible so submitted-but-unevaluated months (e.g. June) don't disappear.
    if (m.status === 'completed' && m.score != null && m.score !== undefined) return true;
    return false;
  });

  return (
    <div className="flex-1" style={{ fontFamily: t.font.family }}>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
        {filteredMonths.map((m, idx) => {
          const isActive = m.status === 'active';
          const isCompleted = m.status === 'completed';
          const isUpcoming = m.status === 'upcoming';
          const isLockedActive = isActive && isOrgAdmin && !isAutonomousMode;
          const isClickable = !m.disabled && !isUpcoming && !isLockedActive;
          const hasScore = m.score != null && m.score !== undefined && parseFloat(m.score) > 0;
          const isRejectedActive = isActive && currentPhase === 'review' && m.hasRejectedGoals;

          const getTooltipTitle = () => {
            if (m.disabled) return 'Submission window is closed. No goals have been added.';
            if (isUpcoming) return 'This month is locked';
            if (isLockedActive) return 'Turn on Unlock editing to access this month';
            if (isActive) {
              if (currentPhase === 'review') return 'View Added Goals';
              if (currentPhase === 'evaluation') return 'View Score';
              if (m.goalsSubmitted) return 'View Submitted Goals';
              return 'Click Here To Add Goals';
            }
            if (isCompleted) return 'Click to view goals for this month';
            return '';
          };

          const handleClick = () => {
            if (!isClickable) return;
            if (onMonthClick) onMonthClick(m.name);
          };

          // Active month carries a colored hairline; everything else is the plain
          // token card so the grid reads like Projects/Team tiles.
          const edge = isActive ? (isRejectedActive ? t.status.negative : t.c.primary) : t.c.border;

          let topRightIcon;
          if (isUpcoming || isLockedActive) {
            topRightIcon = <Lock size={15} strokeWidth={2} style={{ color: t.c.textTertiary }} />;
          } else if (isActive) {
            topRightIcon = <CircleDot size={15} strokeWidth={2} style={{ color: isRejectedActive ? t.status.negative : t.c.primary }} />;
          } else if (isCompleted) {
            topRightIcon = <CheckCircle2 size={15} strokeWidth={2} style={{ color: t.status.positive }} />;
          } else {
            topRightIcon = <TargetIcon size={15} strokeWidth={2} style={{ color: t.c.textTertiary }} />;
          }

          const ctaStyle = {
            display: 'inline-flex', alignItems: 'center', gap: 5,
            fontSize: t.font.size.md, fontWeight: t.font.weight.medium, color: t.c.text,
          };

          let statusBadge = null;
          let cta = null;
          if (isLockedActive) {
            statusBadge = <Badge tone="neutral">Locked</Badge>;
          } else if (isActive) {
            const isRejectedState = isRejectedActive;
            statusBadge = <Badge tone={isRejectedState ? 'negative' : 'brand'}>{isRejectedState ? 'Action needed' : 'Unlocked'}</Badge>;
            cta = (
              <span style={ctaStyle}>
                {isRejectedState
                  ? 'Goals Rejected'
                  : currentPhase === 'review'
                    ? (m.allGoalsApproved ? 'Evaluation Pending' : 'Pending For Review')
                    : currentPhase === 'evaluation'
                      ? 'Evaluation Pending'
                      : m.goalsSubmitted
                        ? 'View Goals'
                        : 'Add Goals'}
                <ArrowRight size={13} />
              </span>
            );
          } else if (isCompleted) {
            statusBadge = <Badge tone="positive">Completed</Badge>;
            cta = (
              <span style={ctaStyle}>
                {hasScore ? `Score ${m.score}/10` : 'View Goals'} <ArrowRight size={13} />
              </span>
            );
          } else {
            statusBadge = <Badge tone="neutral">Locked</Badge>;
          }

          return (
            <Tooltip title={getTooltipTitle()} key={idx}>
              <Card
                interactive={isClickable}
                onClick={handleClick}
                style={{
                  border: `1px solid ${edge}`,
                  minHeight: 88,
                  display: 'flex', flexDirection: 'column', justifyContent: 'space-between', gap: t.s[2],
                  opacity: m.disabled || isUpcoming || isLockedActive ? 0.6 : 1,
                  cursor: isClickable ? 'pointer' : 'not-allowed',
                  position: 'relative',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: t.s[2] }}>
                  <span style={{ fontSize: t.font.size.base, fontWeight: t.font.weight.medium, color: t.c.text }}>{m.name}</span>
                  {m.streak ? (
                    streakIconUrl ? (
                      <img
                        src={streakIconUrl}
                        alt="Streak"
                        style={{ width: 14, height: 14, objectFit: 'cover' }}
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <Flame size={14} style={{ color: t.accentPairs.orange.fg }} />
                    )
                  ) : topRightIcon}
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {statusBadge && <span>{statusBadge}</span>}
                  {cta}
                </div>
              </Card>
            </Tooltip>
          );
        })}
      </div>
    </div>
  );
};

export default MonthlyRoadmap;
